import React from 'react';
import { Card, CardSkeleton, Badge } from '@/components/ui';
import type {
  StepMetrics,
  PerformanceMetrics,
} from '@/services/admin/AdminService';

interface AgentStepMetricsProps {
  metrics: PerformanceMetrics | null;
  loading: boolean;
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const getSuccessRateVariant = (
  rate: number
): 'success' | 'warning' | 'error' => {
  if (rate >= 98) return 'success';
  if (rate >= 95) return 'warning';
  return 'error';
};

const STEP_ORDER = [
  'discovery',
  'design',
  'systems_architect',
  'technical',
  'codegen',
  'operations',
];
const STEP_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  systems_architect: 'Systems Architect',
  technical: 'Technical',
  codegen: 'Code Generation',
  operations: 'Operations',
};

export const AgentStepMetrics: React.FC<AgentStepMetricsProps> = ({
  metrics,
  loading,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (
    !metrics ||
    !metrics.agent_step_metrics ||
    Object.keys(metrics.agent_step_metrics).length === 0
  ) {
    return (
      <Card padding="lg">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">
          Agent Step Performance
        </h3>
        <p className="text-secondary-500">No agent step metrics available</p>
      </Card>
    );
  }

  // Sort steps by predefined order
  const sortedSteps = Object.entries(metrics.agent_step_metrics).sort(
    ([a], [b]) => {
      const indexA = STEP_ORDER.indexOf(a);
      const indexB = STEP_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    }
  );

  return (
    <Card padding="lg">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        Agent Step Performance
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-secondary-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Step
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-secondary-700">
                Avg Duration
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-secondary-700">
                Success Rate
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-secondary-700">
                Total Invocations
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSteps.map(
              ([stepKey, stepMetrics]: [string, StepMetrics]) => (
                <tr
                  key={stepKey}
                  className="border-b border-secondary-100 hover:bg-secondary-50"
                >
                  <td className="py-3 px-4">
                    <span className="font-medium text-secondary-900">
                      {STEP_LABELS[stepKey] || stepMetrics.step_name || stepKey}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="font-mono text-secondary-700">
                      {formatDuration(stepMetrics.avg_duration_ms)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Badge
                      variant={getSuccessRateVariant(stepMetrics.success_rate)}
                    >
                      {stepMetrics.success_rate.toFixed(1)}%
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-secondary-600">
                      {stepMetrics.total_invocations}
                    </span>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default AgentStepMetrics;
